<script lang="ts">
  import FooterCategorySubcomponent from './FooterCategorySubcomponent/FooterCategorySubcomponent.svelte';
  import FooterLanguageDisclaimer from './FooterLanguageDisclaimer.svelte';
  import FooterLinkSubcomponent from './FooterLinkSubcomponent/FooterLinkSubcomponent.svelte';
  import type { FooterComponentProps } from './types';

  export let navigationCategory: FooterComponentProps['navigationCategory'];
  export let globeIcon: FooterComponentProps['globeIcon'];
  export let countryName: FooterComponentProps['countryName'];
  export let languageName: FooterComponentProps['languageName'];
  export let twitterHandle: FooterComponentProps['twitterHandle'];
  export let twitterIcon: FooterComponentProps['twitterIcon'];
  export let facebookHandle: FooterComponentProps['facebookHandle'];
  export let facebookIcon: FooterComponentProps['facebookIcon'];
  export let instagramHandle: FooterComponentProps['instagramHandle'];
  export let instagramIcon: FooterComponentProps['instagramIcon'];
  export let copyright: FooterComponentProps['copyright'];
  export let regulatoryNotice: FooterComponentProps['regulatoryNotice'];
  export let languageDisclaimer: FooterComponentProps['languageDisclaimer'];
  export let legalNotice: FooterComponentProps['legalNotice'];
  export let euCookiePref: FooterComponentProps['euCookiePref'];
  export let naCookiePref: FooterComponentProps['naCookiePref'];
  export let legalNavigationLinks: FooterComponentProps['legalNavigationLinks'];
  export let antiFraudTitle: FooterComponentProps['antiFraudTitle'];
  export let antiFraudUrl: FooterComponentProps['antiFraudUrl'];
  export let accessToDataPrivacyPreferencesRequired: FooterComponentProps['accessToDataPrivacyPreferencesRequired'];
  export let defaultConsentRequired: FooterComponentProps['defaultConsentRequired'];
  export let openLocaleSelectorModal: () => void;

  let showEU = false;
  let showNA = false;
  let showToggle = false;

  $: {
    showEU = Boolean(defaultConsentRequired);
    showNA = !showEU && Boolean(accessToDataPrivacyPreferencesRequired);
    showToggle = showEU || showNA;
  }
</script>

<!-- main section -->
<nav class="mainNav">
  {#if navigationCategory}
    {#each navigationCategory as category}
      <FooterCategorySubcomponent id={category.contentfulEntryId} {...category.props} />
    {/each}
  {/if}
</nav>

<FooterLanguageDisclaimer>{@html languageDisclaimer}</FooterLanguageDisclaimer>

<!-- tertiary section -->
<nav class="tertiaryNav">
  <button id="locale-selector-trigger" on:click={openLocaleSelectorModal} data-modal="locale-selector-modal">
    <!-- eslint-disable-next-line svelte/no-at-html-tags - icon is imported from public-web-icons -->
    {@html globeIcon} &nbsp;{countryName} ({languageName})
  </button>
  <!-- social media nav -->
  <nav class="socialMediaNav">
    <a href={`https://twitter.com/${twitterHandle}`} aria-label="Square Twitter" title="Twitter">
      <!-- eslint-disable-next-line svelte/no-at-html-tags - icon is imported from public-web-icons -->
      {@html twitterIcon}
    </a>
    <a href={`https://www.facebook.com/${facebookHandle}`} aria-label="Square Facebook" title="Facebook">
      <!-- eslint-disable-next-line svelte/no-at-html-tags - icon is imported from public-web-icons -->
      {@html facebookIcon}
    </a>
    <a href={`https://instagram.com/${instagramHandle}`} aria-label="Square Instagram" title="Instagram">
      <!-- eslint-disable-next-line svelte/no-at-html-tags - icon is imported from public-web-icons -->
      {@html instagramIcon}
    </a>
  </nav>
</nav>

<!-- legal section -->
<div class="legalNav">
  <!-- copyright and notices -->
  <div class="notices">
    <p data-testid="copyright">{copyright}</p>
    {#if regulatoryNotice}
      <p data-testid="regulatory-notice">{regulatoryNotice}</p>
    {/if}
    {#if legalNotice}
      <p data-testid="legal-notice">{legalNotice}</p>
    {/if}
  </div>
  <!-- legal nav -->
  <nav class="legalNavLinks">
    <ul class="legalNavLinksList">
      <!-- toggle by one trust module, not by locale -->
      <li class="toggle-one-trust-prefs {showToggle ? '' : 'hidden'}">
        <!-- svelte-ignore a11y-invalid-attribute -->
        <a class="eu-one-trust-link {showEU ? '' : 'hidden'}" href="#">{euCookiePref}</a>
        <!-- svelte-ignore a11y-invalid-attribute -->
        <a class="na-one-trust-link {showNA ? '' : 'hidden'}" href="#">{naCookiePref}</a>
      </li>
      {#each legalNavigationLinks ?? [] as legalLink}
        <FooterLinkSubcomponent
          id={legalLink.contentfulEntryId}
          locale={legalLink.locale}
          pagePath={legalLink.pagePath}
          {...legalLink.props}
        />
      {/each}
      <li><a href="https://block.xyz">Block, Inc.</a></li>
      {#if antiFraudTitle}
        <li><a href={antiFraudUrl}>{antiFraudTitle}</a></li>
      {/if}
    </ul>
  </nav>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/grid.scss' as grid;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;

  @layer standard-components {
    .mainNav {
      @include grid.columns(2, $at: 'small');
      @include grid.columns(4, $at: 'large');
      @include grid.space('none');
      @include grid.space(30, $at: 'small');

      & {
        margin-bottom: var(--space-30);
        text-align: center;

        @media (min-width: vars.$breakpoint-width-small) {
          text-align: left;
        }
      }
    }

    .socialMediaNav {
      @include grid.auto;
      @include grid.space(10);
      @include grid.align('center');
    }

    .legalNav {
      @include grid.columns(1);
      @include grid.space(20);
      @include grid.flex($at: 'medium');
    }

    .legalNavLinksList {
      @include grid.columns(1);
      @include grid.space(10);
    }

    .tertiaryNav {
      @include grid.auto;
      @include grid.flex($at: 'extra-small');
      @include grid.space(20);
      @include grid.align('justify');

      & {
        padding-top: var(--space-20);
        padding-bottom: var(--space-20);
        margin-bottom: var(--space-20);
        border-top: 1px solid var(--color-border-transparent);
        border-bottom: 1px solid var(--color-border-transparent);
      }

      a {
        color: white;
      }

      button {
        border: none;
        background: none;
        color: white;
        cursor: pointer;
        font-family: var(--font-family-base);
        font-size: 100%;
        font-weight: var(--font-weight-medium);
        text-decoration: none;
      }
    }

    .legalNav {
      a {
        color: white;
      }

      .notices {
        min-width: 0;
        width: 100%;
        max-width: 100%;
        text-align: center;

        p {
          font-size: var(--font-size-14);
          line-height: var(--line-height-21);
          color: var(--color-gray-55);
        }
      }
      .legalNavLinks {
        text-align: center;
      }

      .legalNavLinks ul > li {
        padding-left: 0;
      }
      @media (min-width: vars.$breakpoint-width-medium) {
        .notices {
          text-align: left;
        }
        .legalNavLinks {
          text-align: right;
        }
      }
    }
  }
</style>
